/** API service for script upload and analysis. */

import axios, { AxiosResponse } from 'axios';
import {
  UploadResponse,
  AnalyzeResponse,
  ResultsResponse,
  StatusResponse,
  AnalysisResults,
  StreamEvent,
} from '../types/analysis';
import { getUserEmail } from '../utils/userIdentification';
import { auth } from '../lib/firebase';

// API base URL - use environment variable or relative path for vite proxy
// IMPORTANT: If VITE_API_URL is not set, use empty string to leverage Vite proxy
// The proxy forwards /api/* requests to http://localhost:8000
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Debug logging to help diagnose configuration issues
if (import.meta.env.DEV) {
  console.log('[scriptService] API_BASE_URL:', API_BASE_URL || '(empty - using Vite proxy)');
  console.log('[scriptService] VITE_API_URL env var:', import.meta.env.VITE_API_URL || '(not set)');
}

// Create axios instance for script API
const scriptApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 3600000, // 60 minutes — matches Cloud Run request timeout for long analyses
});

// Add request interceptor to include Firebase token
scriptApi.interceptors.request.use(async (config) => {
  if (auth.currentUser) {
    try {
      const token = await auth.currentUser.getIdToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error getting Firebase token:', error);
    }
  }
  return config;
});

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Transient (infra-level) failures that should be retried rather than treated as
 * an analysis failure. While a long-running /analyze request occupies a Cloud Run
 * instance (containerConcurrency=1), polling requests can be rejected with HTTP 429
 * "no available instance" — and because that response lacks the app's CORS headers,
 * the browser surfaces it to axios as a generic "Network Error" with no `response`.
 * 5xx and bare network errors are likewise transient.
 */
function isTransientError(error: any): boolean {
  const status = error?.response?.status;
  if (status === 429) return true;
  if (typeof status === 'number' && status >= 500) return true;
  // No response object => network/CORS/aborted-by-infra (includes the masked 429 above).
  if (!error?.response) return true;
  return false;
}

/** Exponential backoff capped at 8s: 1s, 2s, 4s, 8s, 8s... */
function transientBackoffMs(attempt: number): number {
  return Math.min(8000, 1000 * 2 ** attempt);
}

const STATUS_TRANSIENT_RETRIES = 4;

/**
 * Upload a script file to the server.
 * @param file - The script file to upload
 * @param scriptName - Optional script name (defaults to filename if not provided)
 * @returns Upload response with session ID and script text
 */
export async function uploadScript(
  file: File,
  scriptName?: string,
  signal?: AbortSignal,
  projectId?: string,
): Promise<UploadResponse> {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (scriptName) {
      formData.append('script_name', scriptName);
    }
    if (projectId) {
      formData.append('project_id', projectId);
    }

    const uploadUrl = '/api/scripts/upload';
    if (import.meta.env.DEV) {
      console.log('[scriptService] Upload URL:', uploadUrl);
      console.log('[scriptService] Full URL will be:', API_BASE_URL ? `${API_BASE_URL}${uploadUrl}` : uploadUrl + ' (proxied to localhost:8000)');
    }

    const response: AxiosResponse<UploadResponse> = await scriptApi.post(
      uploadUrl,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 180000, // 3 minutes for upload (GCS uploads can be slow)
        signal,
      }
    );

    if (response.data.status === 'error') {
      throw new Error(response.data.error || 'Upload failed');
    }

    return response.data;
  } catch (error: any) {
    console.error('Error uploading script:', error);
    console.error('Error details:', {
      message: error.message,
      config: {
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        method: error.config?.method,
      },
    });
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error.message || 'Failed to upload script');
  }
}

/**
 * Start analysis in the background; returns immediately with session_id for progress polling.
 * Use getAnalysisStatus(sessionId) for completed_agents and completion; getAnalysisResults when status === 'completed'.
 */
export async function startAnalysis(
  scriptText: string,
  sessionId: string,
  gcsUri?: string,
  scriptName?: string,
  userId?: string,
): Promise<{ session_id: string }> {
  const userEmail = userId || getUserEmail();
  const response = await scriptApi.post<{ status: string; session_id: string }>(
    '/api/scripts/analyze/start',
    {
      script_text: scriptText,
      session_id: sessionId,
      user_id: userEmail,
      gcs_uri: gcsUri,
      script_name: scriptName,
    },
    { timeout: 60000 }
  );
  if (response.status !== 202 || !response.data.session_id) {
    const data = response.data as { session_id?: string; message?: string };
    throw new Error(data?.message || 'Failed to start analysis');
  }
  return { session_id: response.data.session_id };
}

/**
 * Ask the backend to stop an in-flight analysis (upload session_id or ADK session_id from /stream started).
 */
export async function cancelAnalysis(sessionId: string): Promise<{ ok: boolean }> {
  const response = await scriptApi.post<{ ok: boolean; session_id: string }>(
    '/api/scripts/analyze/cancel',
    { session_id: sessionId },
    { timeout: 30000 },
  );
  return { ok: response.data.ok };
}

/**
 * Run analysis and stream progress via Server-Sent Events (agent_start, agent_end, complete, error).
 * Uses fetch() to POST /api/scripts/analyze/stream; invokes onEvent for each parsed event.
 * @param scriptText - Script text to analyze
 * @param sessionId - Session ID (e.g. from upload)
 * @param options - { onEvent: (event) => void } called for each SSE event
 * @param gcsUri - Optional GCS URI for RAG
 * @param scriptName - Optional script name
 * @param userId - Optional user ID (defaults to identified user)
 * @param timeoutMs - Optional overall timeout (default 60 min)
 * @param fallbackPollOnStreamEnd - If the SSE connection drops while the backend keeps running, poll /status until completed or error
 */
export async function analyzeScriptStream(
  scriptText: string,
  sessionId: string,
  options: { onEvent: (event: StreamEvent) => void; signal?: AbortSignal },
  gcsUri?: string,
  scriptName?: string,
  userId?: string,
  timeoutMs: number = 3600000,
  fallbackPollOnStreamEnd: boolean = true,
  projectId?: string,
): Promise<void> {
  const userEmail = userId || getUserEmail();
  // Backend creates a NEW ADK session and ignores the client-provided session_id.
  // Keep a mutable session id for fallback polling.
  let currentSessionId = sessionId;
  let token: string | undefined;
  if (auth.currentUser) {
    try {
      token = await auth.currentUser.getIdToken();
    } catch {
      // proceed without token
    }
  }
  const url = `${API_BASE_URL || ''}/api/scripts/analyze/stream`;
  const controller = new AbortController();
  let abortedByUser = false;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const clearTimer = () => {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  };
  if (options.signal) {
    const u = options.signal;
    const onUserAbort = () => {
      abortedByUser = true;
      clearTimer();
      controller.abort();
    };
    if (u.aborted) {
      onUserAbort();
    } else {
      u.addEventListener('abort', onUserAbort, { once: true });
    }
  }
  timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  let streamFinishedCleanly = false;

  const pollAfterDisconnect = async () => {
    if (!fallbackPollOnStreamEnd || !currentSessionId) return;
    const intervalMs = 4000;
    const deadline = Date.now() + timeoutMs;
    let lastSig = '';
    while (Date.now() < deadline) {
      const status = await getAnalysisStatus(currentSessionId, userEmail);
      // Only sync progress when the status actually carries agent state. A transient
      // "processing" fallback (from a retried 429/network blip) omits completed_agents,
      // so we must not reset the progress bar to empty.
      if (status.completed_agents || status.completed_phases) {
        const agents = status.completed_agents ?? [];
        const phases = status.completed_phases ?? [];
        const sig = `${agents.join('\0')}\x1e${phases.join('\0')}`;
        if (sig !== lastSig) {
          lastSig = sig;
          options.onEvent({
            type: 'progress_sync',
            completed_agents: [...agents],
            completed_phases: phases.length ? [...phases] : undefined,
          });
        }
      }
      if (status.status === 'completed') {
        options.onEvent({ type: 'complete', session_id: currentSessionId });
        streamFinishedCleanly = true;
        return;
      }
      if (status.status === 'error') {
        options.onEvent({
          type: 'error',
          message: status.error || 'Analysis failed',
        });
        streamFinishedCleanly = true;
        return;
      }
      await new Promise((r) => setTimeout(r, intervalMs));
    }
    options.onEvent({
      type: 'error',
      message:
        'Connection to the analysis stream was lost and status polling timed out. The run may still finish—use Retrieve Previous Analysis.',
    });
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({
        script_text: scriptText,
        session_id: sessionId,
        user_id: userEmail,
        gcs_uri: gcsUri ?? undefined,
        script_name: scriptName ?? undefined,
        project_id: projectId ?? undefined,
      }),
      signal: controller.signal,
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Stream request failed: ${response.status}`);
    }
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }
    const decoder = new TextDecoder();
    let buffer = '';
    try {
      for (;;) {
        let chunk: ReadableStreamReadResult<Uint8Array>;
        try {
          chunk = await reader.read();
        } catch {
          break;
        }
        const { done, value } = chunk;
        buffer += decoder.decode(value ?? new Uint8Array(0), { stream: !done });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          const trimmed = line.trim();
          if (trimmed.startsWith('data:')) {
            const jsonStr = trimmed.slice(5).trim();
            if (!jsonStr) continue;
            try {
              const event = JSON.parse(jsonStr) as StreamEvent;
              if (event.type === 'complete') {
                console.log('[analyzeScriptStream] Received complete event session_id=', event.session_id);
              }
              if (event.type === 'started') {
                currentSessionId = event.session_id;
              }
              if (event.type === 'complete') {
                currentSessionId = event.session_id;
              }
              options.onEvent(event);
              if (
                event.type === 'complete' ||
                event.type === 'error' ||
                event.type === 'cancelled'
              ) {
                streamFinishedCleanly = true;
                return;
              }
            } catch (e) {
              console.warn('SSE parse error:', e);
            }
          }
        }
        if (done) break;
      }
    } finally {
      try {
        reader.releaseLock();
      } catch {
        /* ignore */
      }
    }
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    const abortLike =
      (err instanceof Error && err.name === 'AbortError') ||
      /abort|BodyStream|network|Failed to fetch|fetch/i.test(msg);
    if (!abortLike) {
      throw err;
    }
    if (abortedByUser) {
      return;
    }
    console.warn('[analyzeScriptStream] Stream error (will poll if enabled):', msg);
  } finally {
    clearTimer();
  }

  if (!streamFinishedCleanly && !abortedByUser) {
    await pollAfterDisconnect();
  }
}

/**
 * Analyze a script using the Engagement Predictor agents (synchronous; waits until complete).
 * For progress steps on upload page, use analyzeScriptStream or startAnalysis + poll status for completed_agents.
 * @param scriptText - The script text to analyze (required for backward compatibility)
 * @param sessionId - Optional session ID (if provided, uses existing session)
 * @param userId - User ID (optional, defaults to identified user email)
 * @returns Analysis response with session ID
 */
export async function analyzeScript(
  scriptText: string,
  sessionId?: string,
  userId?: string,
  gcsUri?: string,
  scriptName?: string,
): Promise<AnalyzeResponse> {
  // Use provided userId or get from user identification
  const userEmail = userId || getUserEmail();
  try {
    const response: AxiosResponse<AnalyzeResponse> = await scriptApi.post(
      '/api/scripts/analyze',
      {
        script_text: scriptText,
        session_id: sessionId,
        user_id: userEmail,
        gcs_uri: gcsUri,
        script_name: scriptName,
      },
      {
        timeout: 3600000, // 60 minutes — matches Cloud Run request timeout
      }
    );

    if (response.data.status === 'error') {
      throw new Error(response.data.error || 'Analysis failed');
    }

    return response.data;
  } catch (error: any) {
    console.error('Error analyzing script:', error);
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error.message || 'Failed to analyze script');
  }
}

export type StepReviewLevel = 'ok' | 'fallthrough' | 'failed';

export type StepReviewItem = {
  step_id: string;
  agent_id?: string | null;
  strand_id?: string | null;
  executor_kind?: string | null;
  executor_id?: string | null;
  status?: string | null;
  level: StepReviewLevel;
  label: string;
  detail?: string;
  created_at?: string | null;
  fell_through?: boolean;
  fallthrough_errors?: string[];
  error?: string | null;
  inputs_ref?: string[];
  methodology_version?: string | null;
  executor_attempts?: unknown[];
  output?: Record<string, unknown>;
};

export type SessionStepsResponse = {
  status: string;
  session_id: string;
  script_id: string;
  script_title?: string | null;
  step_count: number;
  ok_count: number;
  fallthrough_count: number;
  failed_count: number;
  steps: StepReviewItem[];
};

/**
 * List methodology step outputs for a session (Step Review before scorecard).
 */
export async function getSessionSteps(
  sessionId: string,
  options?: { includeOutput?: boolean }
): Promise<SessionStepsResponse> {
  try {
    const response: AxiosResponse<SessionStepsResponse> = await scriptApi.get(
      `/api/scripts/${sessionId}/steps`,
      {
        params: {
          include_output: options?.includeOutput !== false,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    const detail =
      error?.response?.data?.detail ||
      error?.message ||
      'Failed to load step outputs';
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
}

/**
 * Get analysis results for a session.
 * @param sessionId - The session ID
 * @param userId - User ID (optional, defaults to identified user email)
 * @returns Analysis results
 */
export async function getAnalysisResults(
  sessionId: string,
  userId?: string
): Promise<AnalysisResults> {
  // Use provided userId or get from user identification
  const userEmail = userId || getUserEmail();
  let lastError: any;
  for (let attempt = 0; attempt <= STATUS_TRANSIENT_RETRIES; attempt++) {
    try {
      const response: AxiosResponse<ResultsResponse> = await scriptApi.get(
        `/api/scripts/${sessionId}/results`,
        {
          params: { user_id: userEmail },
        }
      );

      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to get results');
      }

      return response.data.results;
    } catch (error: any) {
      lastError = error;
      // Retry only infra-level blips (429 "no available instance"/5xx/network).
      if (isTransientError(error) && attempt < STATUS_TRANSIENT_RETRIES) {
        const wait = transientBackoffMs(attempt);
        console.warn(
          `[getAnalysisResults] transient error (${error?.response?.status ?? 'network'}); retry ${
            attempt + 1
          }/${STATUS_TRANSIENT_RETRIES} in ${wait}ms`
        );
        await sleep(wait);
        continue;
      }
      break;
    }
  }
  console.error('Error getting analysis results:', lastError);
  const detail = lastError?.response?.data?.detail;
  if (detail && typeof detail === 'object' && detail.message) {
    const err = new Error(String(detail.message)) as Error & {
      code?: string;
      scorecardPending?: boolean;
    };
    err.code = detail.code;
    err.scorecardPending = Boolean(detail.scorecard_pending);
    throw err;
  }
  if (typeof detail === 'string') {
    throw new Error(detail);
  }
  throw new Error(lastError?.message || 'Failed to get analysis results');
}

/**
 * Check analysis status for a session.
 * @param sessionId - The session ID
 * @param userId - User ID (optional, defaults to identified user email)
 * @returns Status response
 */
export async function getAnalysisStatus(
  sessionId: string,
  userId?: string
): Promise<StatusResponse> {
  // Use provided userId or get from user identification
  const userEmail = userId || getUserEmail();
  let lastError: any;
  for (let attempt = 0; attempt <= STATUS_TRANSIENT_RETRIES; attempt++) {
    try {
      const response: AxiosResponse<StatusResponse> = await scriptApi.get(
        `/api/scripts/${sessionId}/status`,
        {
          params: { user_id: userEmail },
        }
      );
      return response.data;
    } catch (error: any) {
      lastError = error;
      if (isTransientError(error) && attempt < STATUS_TRANSIENT_RETRIES) {
        const wait = transientBackoffMs(attempt);
        console.warn(
          `[getAnalysisStatus] transient error (${error?.response?.status ?? 'network'}); retry ${
            attempt + 1
          }/${STATUS_TRANSIENT_RETRIES} in ${wait}ms`
        );
        await sleep(wait);
        continue;
      }
      break;
    }
  }
  // A transient failure must NOT abort an in-flight analysis: report "processing"
  // (with no completed_agents field, so callers don't reset progress) and let the
  // caller keep polling. Only genuine app errors are reported as status: 'error'.
  if (isTransientError(lastError)) {
    console.warn('[getAnalysisStatus] transient errors persisted; treating as still processing');
    return { session_id: sessionId, status: 'processing' };
  }
  console.error('Error getting analysis status:', lastError);
  return {
    session_id: sessionId,
    status: 'error',
    error: lastError?.message || 'Failed to get status',
  };
}

/**
 * Poll for analysis results until completed or error.
 * @param sessionId - The session ID
 * @param userId - User ID (optional, defaults to identified user email)
 * @param maxAttempts - Maximum number of polling attempts (default: 720 = 60 min at 5s)
 * @param intervalMs - Polling interval in milliseconds (default: 5000)
 * @returns Analysis results when complete
 */
export async function pollForResults(
  sessionId: string,
  userId?: string,
  maxAttempts: number = 720, // 720 × 5s = 60 minutes (allow backend to finish long runs)
  intervalMs: number = 5000
): Promise<AnalysisResults> {
  return pollForResultsWithProgress(sessionId, {}, userId, maxAttempts, intervalMs);
}

/**
 * Poll for analysis results and call onProgress with each status (for step progress UI).
 */
export async function pollForResultsWithProgress(
  sessionId: string,
  options: { onProgress?: (status: StatusResponse) => void },
  userId?: string,
  maxAttempts: number = 360,
  intervalMs: number = 5000
): Promise<AnalysisResults> {
  const userEmail = userId || getUserEmail();
  let attempts = 0;

  while (attempts < maxAttempts) {
    try {
      const status = await getAnalysisStatus(sessionId, userEmail);
      options.onProgress?.(status);

      if (status.status === 'completed') {
        return await getAnalysisResults(sessionId, userEmail);
      }
      if (status.status === 'error' || status.status === 'not_found') {
        throw new Error(status.error || 'Analysis failed or not found');
      }

      await new Promise((resolve) => setTimeout(resolve, intervalMs));
      attempts++;
    } catch (error: any) {
      if (error.message && !error.message.includes('not found')) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
      attempts++;
    }
  }

  throw new Error('Analysis timed out. Please try again later.');
}
